import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { EdaLabelStats } from '../../types';

interface LabelDistributionChartProps {
    labelName: string;
    labelStats: EdaLabelStats;
    histogramBins?: number[];
    histogramEdges?: number[];
    quartiles?: { q1: number; median: number; q3: number };
    mean?: number;
    isCategorical?: boolean;
    barColor?: string;
}

// Hilfsfunktion: Histogramm-Daten für numerische Labels aufbereiten (wie bei FeatureDistributionChart)
const prepareHistogramData = (bins: number[] = [], edges: number[] = []) => {
    if (!bins.length || !edges.length || bins.length !== edges.length - 1) return [];
    return bins.map((count, i) => ({
        name: `${edges[i].toFixed(2)} - ${edges[i + 1].toFixed(2)}`,
        Anzahl: count,
        binStart: edges[i],
        binEnd: edges[i + 1],
    }));
};

// Custom Tooltip analog zu FeatureDistributionChart
const CustomTooltipContent = ({ active, payload, label, labelStats, quartiles }: any) => {
    if (active && payload && payload.length && labelStats && quartiles) {
        const data = payload[0].payload;
        return (
            <div className="custom-tooltip">
                <p className="label">{`Bin: ${data.name}`}</p>
                <p className="intro">{`Anzahl: ${payload[0].value}`}</p>
                <hr />
                <p className="desc"><strong>Statistiken (Label):</strong></p>
                <p>{`Mean: ${labelStats.mean?.toFixed(2) ?? 'N/A'}`}</p>
                <p>{`Median: ${quartiles.median.toFixed(2)}`}</p>
                <p>{`Q1: ${quartiles.q1.toFixed(2)}`}</p>
                <p>{`Q3: ${quartiles.q3.toFixed(2)}`}</p>
                <p>{`Std: ${labelStats.std?.toFixed(2) ?? 'N/A'}`}</p>
            </div>
        );
    }
    return null;
};

export const LabelDistributionChart: React.FC<LabelDistributionChartProps> = ({
    labelName,
    labelStats,
    histogramBins,
    histogramEdges,
    quartiles,
    mean,
    isCategorical = false,
    barColor = '#4CAF50',
}) => {
    // Nur numerische Labels werden unterstützt
    const data = useMemo(() => prepareHistogramData(histogramBins, histogramEdges), [histogramBins, histogramEdges]);
    // Domain wie bei FeatureDistributionChart
    const finalDomain = useMemo((): [number, number] => {
        if (!histogramEdges || histogramEdges.length < 2) return [0, 1];
        const xMin = histogramEdges[0];
        const xMax = histogramEdges[histogramEdges.length - 1];
        const q1 = quartiles?.q1 ?? xMin;
        const q3 = quartiles?.q3 ?? xMax;
        const meanVal = mean ?? xMin;
        const domainMin = Math.min(xMin, q1, meanVal);
        const domainMax = Math.max(xMax, q3, meanVal);
        const domainPadding = (domainMax - domainMin) * 0.05;
        if (domainMax === domainMin) return [domainMin - 1, domainMax + 1];
        return [domainMin - domainPadding, domainMax + domainPadding];
    }, [histogramEdges, quartiles, mean]);

    if (!data.length) {
        return (
            <div className="chart-container card label-distribution-chart">
                <h4>Label-Verteilung: {labelName}</h4>
                <p>Keine Histogramm-Daten für dieses Label vorhanden.</p>
            </div>
        );
    }

    return (
        <div className="chart-container card label-distribution-chart">
            <div className="chart-header-controls">
                <h3>Label-Verteilung</h3>
                <span style={{ fontWeight: 500, marginLeft: 12 }}>{labelName}</span>
            </div>
            <ResponsiveContainer width="100%" height={350}>
                <BarChart
                    data={data}
                    margin={{ top: 20, right: 40, left: 20, bottom: 60 }}
                    barCategoryGap="0%"
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey="binStart"
                        type="number"
                        scale="linear"
                        domain={finalDomain}
                        tickFormatter={(tick) => tick.toFixed(1)}
                        angle={-45}
                        textAnchor="end"
                        height={70}
                        label={{ value: labelName, position: 'insideBottom', dy: 40 }}
                    />
                    <YAxis label={{ value: 'Anzahl', angle: -90, position: 'insideLeft' }} allowDecimals={false} />
                    <Tooltip content={(props) => CustomTooltipContent({ ...props, labelStats, quartiles })} />
                    <Bar dataKey="Anzahl" fill={barColor} />
                    {/* ReferenceLines für Mittelwert, Median, Q1, Q3 */}
                    {mean !== undefined && (
                        <ReferenceLine x={mean} stroke="#e91e63" strokeWidth={2} label={{ value: `Mean: ${mean.toFixed(2)}`, position: 'insideTopRight', fill: '#e91e63', fontSize: 10 }} />
                    )}
                    {quartiles?.median !== undefined && (
                        <ReferenceLine x={quartiles.median} stroke="#ff9800" strokeWidth={2} label={{ value: `Median: ${quartiles.median.toFixed(2)}`, position: 'insideTopLeft', fill: '#ff9800', fontSize: 10 }} />
                    )}
                    {quartiles?.q1 !== undefined && (
                        <ReferenceLine x={quartiles.q1} stroke="#9c27b0" strokeDasharray="3 3" strokeWidth={1} label={{ value: `Q1: ${quartiles.q1.toFixed(2)}`, position: 'bottom', fill: '#9c27b0', fontSize: 10 }} />
                    )}
                    {quartiles?.q3 !== undefined && (
                        <ReferenceLine x={quartiles.q3} stroke="#9c27b0" strokeDasharray="3 3" strokeWidth={1} label={{ value: `Q3: ${quartiles.q3.toFixed(2)}`, position: 'bottom', fill: '#9c27b0', fontSize: 10 }} />
                    )}
                </BarChart>
            </ResponsiveContainer>
            <div className="label-stats-summary">
                <span>Min: {labelStats.min !== undefined && labelStats.min !== null ? Number(labelStats.min).toFixed(2) : 'N/A'}</span>{' | '}
                <span>Max: {labelStats.max !== undefined && labelStats.max !== null ? Number(labelStats.max).toFixed(2) : 'N/A'}</span>{' | '}
                <span>Mittelwert: {labelStats.mean !== undefined && labelStats.mean !== null ? Number(labelStats.mean).toFixed(2) : 'N/A'}</span>{' | '}
                <span>Median: {labelStats.median !== undefined && labelStats.median !== null ? Number(labelStats.median).toFixed(2) : 'N/A'}</span>{' | '}
                <span>Std: {labelStats.std !== undefined && labelStats.std !== null ? Number(labelStats.std).toFixed(2) : 'N/A'}</span>
            </div>
        </div>
    );
}; 